import { Suspense, useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router";
import { AppProvider } from "../context/AppProvider";
import { AuthProvider } from "../context/AuthProvider";
import TopBelt from "../components/TopBelt";
import Sidebar from "../components/Sidebar";
import { DESKTOP_BREAKPOINT } from "../config/constants";
import AgeGatePrompt from "../components/AgeGatePrompt";
import {
  confirmAgeGate,
  hasConfirmedAgeGate,
} from "../services/ageGateService";
import CookieConsentBanner from "../components/CookieConsentBanner";
import { ensureRegionInfo, type RegionInfo } from "../services/regionService";
import {
  getStoredConsent,
  isMarketingAllowed,
  type CookieConsent,
} from "../services/cookieConsentService";
import {
  captureFbclidFromUrl,
  initializeFbclidTracking,
  persistCurrentFbclidToFirestore,
} from "../services/fbclidService";
import {
  initializeUTMTracking,
  persistCurrentUtmAttributionToFirestore,
} from "../services/utmService";
import {
  initializeMetaPixel,
  isMetaPixelReady,
  revokeMetaPixelConsent,
  trackPixelPageView,
} from "../services/metaPixelService";
import { logEmailArrivalIfPresent } from "../services/emailArrivalTracking";
import { logAffiliateArrivalIfPresent } from "../services/affiliateArrivalTracking";
import { captureTrafficSourceFromUrl } from "../services/trafficSourceService";
import {
  logEvent,
  logPageView,
  notifyAnalyticsContextUpdated,
} from "../services/loggingService";
import { useAuth } from "../context/AuthProvider";

function AppLayout() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= DESKTOP_BREAKPOINT : true
  );
  const [ageGateStatus, setAgeGateStatus] = useState<
    "pending" | "confirmed" | "blocked"
  >(() => (hasConfirmedAgeGate() ? "confirmed" : "pending"));
  const [regionInfo, setRegionInfo] = useState<RegionInfo | null>(null);
  const [consentState, setConsentState] = useState<CookieConsent | null>(
    getStoredConsent()
  );
  // Location key covered by the PageView that initializeMetaPixel already
  // fired on the pixel's first successful init, so the route-change effect
  // below can skip re-sending a PageView for that same route.
  const pixelPageViewKeyRef = useRef<string | null>(null);

  useEffect(() => {
    function handleResize() {
      const desktop = window.innerWidth >= DESKTOP_BREAKPOINT;
      setIsDesktop(desktop);
      // Close mobile sidebar when switching to desktop
      if (desktop) {
        setIsSidebarOpen(false);
      }
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    void ensureRegionInfo()
      .then(setRegionInfo)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const targetId = decodeURIComponent(location.hash.replace("#", ""));
    let attempts = 0;
    const maxAttempts = 20;

    const intervalId = window.setInterval(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        window.clearInterval(intervalId);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 100);

    return () => window.clearInterval(intervalId);
  }, [location.hash, location.pathname]);

  useEffect(() => {
    captureFbclidFromUrl();
    captureTrafficSourceFromUrl();
  }, [location.search]);

  useEffect(() => {
    if (!regionInfo) {
      return;
    }
    if (isMarketingAllowed(regionInfo.regionBucket, consentState)) {
      initializeUTMTracking();
      initializeFbclidTracking();
      const wasPixelReady = isMetaPixelReady();
      initializeMetaPixel(regionInfo.regionBucket, consentState);
      if (!wasPixelReady && isMetaPixelReady()) {
        // initializeMetaPixel fires its own PageView for the current route;
        // remember it so the route-change effect doesn't duplicate it.
        pixelPageViewKeyRef.current = `${location.pathname}${location.search}`;
      }
    } else {
      revokeMetaPixelConsent();
    }
    // location is read only to label the PageView that initializeMetaPixel just
    // fired, and must stay out of the dependency list: re-running this effect on
    // every navigation would also re-run initializeUTMTracking/
    // initializeFbclidTracking, which are meant to run once per consent change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionInfo, consentState]);

  useEffect(() => {
    if (!regionInfo) {
      return;
    }
    if (isMarketingAllowed(regionInfo.regionBucket, consentState)) {
      void logAffiliateArrivalIfPresent();
    }
  }, [regionInfo, consentState, location.search]);

  useEffect(() => {
    if (ageGateStatus !== "confirmed") {
      return;
    }
    void logPageView(`${location.pathname}${location.search}`);

    const currentPixelPageViewKey = `${location.pathname}${location.search}`;
    if (pixelPageViewKeyRef.current === currentPixelPageViewKey) {
      // Already covered by the PageView initializeMetaPixel fired on init.
      return;
    }
    trackPixelPageView();
    pixelPageViewKeyRef.current = currentPixelPageViewKey;
  }, [ageGateStatus, location.pathname, location.search]);

  useEffect(() => {
    notifyAnalyticsContextUpdated();
  }, [regionInfo?.regionBucket, consentState?.analytics, consentState?.updatedAt]);

  useEffect(() => {
    const userId = user?.uid;
    if (authLoading || !userId || !regionInfo) {
      return;
    }
    if (!isMarketingAllowed(regionInfo.regionBucket, consentState)) {
      return;
    }
    void persistCurrentFbclidToFirestore(userId);
    void persistCurrentUtmAttributionToFirestore(userId);
  }, [authLoading, user?.uid, regionInfo, consentState]);

  useEffect(() => {
    if (authLoading || !user || user.isAnonymous) {
      return;
    }
    void logEmailArrivalIfPresent();
  }, [authLoading, user, location.pathname, location.search]);

  const handleMenuToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-black pt-[57px]"
      style={{ minHeight: "var(--app-height)" }}
    >
      {ageGateStatus !== "confirmed" && (
        <AgeGatePrompt
          mode={ageGateStatus === "blocked" ? "blocked" : "gate"}
          onConfirm={() => {
            confirmAgeGate();
            void logEvent("age_gate_confirmed");
            setAgeGateStatus("confirmed");
          }}
          onDecline={() => {
            setAgeGateStatus("blocked");
          }}
        />
      )}
      {ageGateStatus === "confirmed" && (
        <CookieConsentBanner
          regionInfo={regionInfo}
          onConsentUpdated={(consent) => {
            void logEvent("cookie_consent_updated", {
              analytics: consent?.analytics ?? false,
              marketing: consent?.marketing ?? false,
            });
            setConsentState(consent);
          }}
        />
      )}
      <TopBelt
        onMenuToggle={handleMenuToggle}
        isSidebarOpen={isSidebarOpen}
      />
      <div className="flex flex-1">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={handleSidebarClose}
          isDesktop={isDesktop}
        />
        <main className="flex-1 min-w-0">
          {ageGateStatus === "confirmed" ? (
            <Suspense fallback={<div className="min-h-screen bg-black" />}>
              <Outlet />
            </Suspense>
          ) : (
            <div className="min-h-screen bg-black" />
          )}
        </main>
      </div>
    </div>
  );
}

export default function AppRoot() {
  return (
    <AuthProvider>
      <AppProvider>
        <AppLayout />
      </AppProvider>
    </AuthProvider>
  );
}
